import { useMutation, useQuery } from "@tanstack/react-query"
import { queryKeys } from "~/constants/queryKeys"
import api from "~/api2"
import { useNavigate } from "react-router-dom"
import { ROUTER_PATHS } from "~/routes/constants"

export const useDynamicFormsResource = (modelName?: string, campId?: string, q?: string, id?: string, page = 1, perPage: 25 | 50 | 100 | 200 = 50,) => {
  const navigate = useNavigate()
  
  const fetchResourceIndexQuery = useQuery({
    queryKey: [queryKeys.DYNAMIC_FORMS_RESOURCE_INDEX, modelName, campId, perPage, q, page],
    queryFn: async () => {
      const modelSlug = modelName.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')
      try {
        const response = await api.DynamicForms.fetchAll(modelSlug, campId, perPage, q, page)
        return response || { records: [], total: 0 }
      } catch (error: any) {
        if (error?.status === 403 || error?.status === 401) {
          navigate(ROUTER_PATHS.UNAUTHORIZED)
          return { records: [], total: 0 }
        }
        throw error
      }
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const modelSlug = modelName.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')
      try {
        const response = await api.DynamicForms.update(modelSlug, campId, id, data)
        return response
      } catch (error: any) {
        if (error?.status === 403 || error?.status === 401) {
          navigate(ROUTER_PATHS.UNAUTHORIZED)
        }
        console.error("Error updating resource:", error)
        throw error
      }
    },
    onSuccess: () => {
      fetchResourceIndexQuery.refetch()
    },
    onError: (error: any) => {
      console.error("Error in update mutation:", error)
      // Optionally, you can handle error notifications here
    }
  })

  const destroyMutation = useMutation({
    mutationFn: async (id: string) => {
      const modelSlug = modelName.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')
      try {
        const response = await api.DynamicForms.remove(modelSlug, campId, id)
        return response
      } catch (error: any) {
        if (error?.status === 403 || error?.status === 401) {
          navigate(ROUTER_PATHS.UNAUTHORIZED)
        }
        console.error("Error deleting resource:", error)
        throw error
      }
    },
    onSuccess: () => {
      fetchResourceIndexQuery.refetch()
    },
    onError: (error: any) => {
      console.error("Error in destroy mutation:", error)
      // Optionally, you can handle error notifications here
    }
  })

    
  const fetchResourceShowQuery = useQuery({
    queryKey: [queryKeys.DYNAMIC_FORMS_RESOURCE_SHOW, modelName, campId, id],
    refetchOnWindowFocus: true,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
    queryFn: async () => {
      const modelSlug = modelName.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')
      try {
        const response = await api.DynamicForms.show(modelSlug, campId, id)
        return response
      } catch (error: any) {
        if (error?.status === 403 || error?.status === 401) {
          navigate(ROUTER_PATHS.UNAUTHORIZED)
          return { record: null }
        }
        throw error
      }
    },
    enabled: !!id && !!campId && !!modelName,
  })

  return {
    index: fetchResourceIndexQuery,
    update: updateMutation,
    destroy: destroyMutation,
    show: fetchResourceShowQuery,
  }
}
